// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';
import {
  NavigateFunction,
  useNavigate,
} from 'react-router-dom';

// Hooks
import { useBreakpoint } from '../../hooks/useBreakpoint';

// Store
import { getUser } from '../../store/session.store';

// Types
import { User, UserType } from '../../types/global';

// Components
import { AdminPanel } from '../../layout/AdminPanel';
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonType,
} from '../../layout/elements/Button';
import { Col } from '../../layout/elements/Col';
import { CustomerPanel } from '../../layout/CustomerPanel';
import { PageHeader } from '../../layout/elements/PageHeader';
import { Row } from '../../layout/elements/Row';
import { ArrowLeft } from '../../layout/icons';

// Module
import { LanguageModule } from './LanguageModule';
import { MeasurementModule } from './MeasurementModule';
import { SettingsModule } from './SettingsModule';
import { UserAgreements } from './UserAgreements';

// View
import {
  MobileBackHeader,
  PageHeaderContainer,
  PanelContainer,
  ProfileSettingsContainer,
} from './Settings.style';

function Settings(): JSX.Element | null {
  // Dependencies
  const { t } = useTranslation();
  const navigate: NavigateFunction = useNavigate();
  const {
    isXs,
    isSm,
    isMd,
    isLg,
  } = useBreakpoint();

  // Getter
  const user: User | null = getUser();

  // Determine if we're in mobile view
  const isMobileView: boolean = isXs || isSm || isMd || isLg;

  /* ***********************************************************************************************
  **************************************** INITIAL STATE *******************************************
  *********************************************************************************************** */

  /* ***********************************************************************************************
  ****************************************** METHODS ***********************************************
  *********************************************************************************************** */

  // Redirects if user is not found
  if (!user) {
    navigate('/plant-management');
    return null;
  }

  const profileSettingsContent = (
    <ProfileSettingsContainer data-testid="profile-settings-container">
      {/* Mobile back button - only visible when SideNavigation is hidden */}
      {isMobileView && (
        <MobileBackHeader data-testid="mobile-back-header">
          <Button
            onClick={() => navigate(-1)}
            type={ButtonType.link}
            size={ButtonSize.xs}
            block={false}
            color={ButtonColor.neutral}
            testId="settings-back-button"
          >
            <ArrowLeft />
            {t('common.back')}
          </Button>
        </MobileBackHeader>
      )}
      <PageHeaderContainer data-testid="page-header-container">
        <PageHeader
          title={t('view.settings.profileTitle')}
          description={t('view.settings.profileDescription')}
        />
      </PageHeaderContainer>
      <LanguageModule testId="language-module" />
      <MeasurementModule testId="measurement-module" />
      <UserAgreements testId="user-agreements-module" />
    </ProfileSettingsContainer>
  );

  return user.type === UserType.admin
    ? (
      <AdminPanel>
        <Row>
          <SettingsModule testId="settings-module" />
          <Col xs={24} xl={20}>
            <PanelContainer data-testid="settings-panel-container">
              {profileSettingsContent}
            </PanelContainer>
          </Col>
        </Row>
      </AdminPanel>
    ) : (
      <CustomerPanel>
        <Row>
          <SettingsModule testId="settings-module" />
          <Col xs={24} xl={20}>
            <PanelContainer data-testid="settings-panel-container">
              {profileSettingsContent}
            </PanelContainer>
          </Col>
        </Row>
      </CustomerPanel>
    );
}

export { Settings };
